import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Warehouse, Settings, Info, CheckCircle2, HelpCircle, Loader2, TestTube2, Monitor, ArrowLeftRight, ArrowRight, MapPin, Database, Link as LinkIcon, AlertTriangle, XCircle, Tag, Eye, Save, Download, Upload, Building2, Package, X } from "lucide-react";
import { toast } from "sonner";
import {
  WarehouseHierarchyConfig,
  HierarchyLevel,
  DEFAULT_HIERARCHY_CONFIG,
  LEVEL_PLACEHOLDER_EXAMPLES,
  generateLocationCodePreview,
  ValidationError,
} from "@/types/warehouse-config";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useScreenMode } from "@/providers/ModeProvider";
import { useGraphQLFetch } from "@/hooks/use-graphql-fetch";

interface LocationHierarchyLevelFromAPI {
  levelID: number;
  levelNumber: number;
  displayName: string;
  codePrefix: string | null;
  isActive: boolean;
}

const GET_LOCATION_HIERARCHY_CONFIG = `
  query GetLocationHierarchyConfig {
    locationHierarchy {
      locationHierarchyConfig {
        levelID
        levelNumber
        displayName
        codePrefix
        isActive
      }
    }
  }
`;

const UPDATE_LOCATION_HIERARCHY_LEVEL = `
  mutation UpdateLocationHierarchyLevel($level: LocationHierarchyLevelInput!) {
    locationHierarchy {
      locationHierarchyConfig {
        updateLevel(level: $level) {
          levelID
          levelNumber
          displayName
          codePrefix
          isActive
        }
      }
    }
  }
`;

export function WarehouseLocationSetupPage() {
  const { mode, setMode, isDemo, isLive } = useScreenMode("warehouse-location-setup");
  const { fetchGraphQL } = useGraphQLFetch();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("hierarchy");
  
  // ERP Integration state
  const [erpSystem, setErpSystem] = useState<string | null>(null);
  const [erpDatabase, setErpDatabase] = useState<string | null>(null);
  
  // ERP Field Mapping state
  type MappingOption = 'not-used' | 'warehouse' | 'aisle' | 'rack' | 'shelf' | 'bin' | 'concatenated' | 'product-category' | 'custom-field';
  
  const [fieldMappings, setFieldMappings] = useState<Record<string, MappingOption>>({
    'subAnalysisCode': 'not-used',
    'userSort1': 'not-used',
    'userSort2': 'not-used',
    'userSort3': 'not-used',
    'userSort4': 'not-used',
    'userSort5': 'not-used',
    'userSort6': 'not-used',
    'binNumber': 'not-used',
    'sortKey': 'not-used',
  });
  
  // Advanced tracking checkboxes
  const [usedForBatchTracking, setUsedForBatchTracking] = useState(false);
  const [usedForVariants, setUsedForVariants] = useState(false);
  
  // Modal states
  const [showSampleConfigModal, setShowSampleConfigModal] = useState(false);
  const [showConfirmLoadDialog, setShowConfirmLoadDialog] = useState(false);
  const [selectedSampleConfig, setSelectedSampleConfig] = useState<string | null>(null);
  
  // Validation helpers
  const getFieldLabel = (fieldKey: string): string => {
    const labels: Record<string, string> = {
      'subAnalysisCode': 'Sub Analysis Code',
      'userSort1': 'User Sort 1',
      'userSort2': 'User Sort 2',
      'userSort3': 'User Sort 3',
      'userSort4': 'User Sort 4',
      'userSort5': 'User Sort 5',
      'userSort6': 'User Sort 6',
      'binNumber': 'Bin Number',
      'sortKey': 'Sort Key',
    };
    return labels[fieldKey] || fieldKey;
  };
  
  const getMappedField = (targetMapping: MappingOption): string | null => {
    const locationLevelMappings: MappingOption[] = ['warehouse', 'aisle', 'rack', 'shelf', 'bin'];
    if (!locationLevelMappings.includes(targetMapping)) return null;
    
    for (const [fieldKey, mapping] of Object.entries(fieldMappings)) {
      if (mapping === targetMapping) {
        return getFieldLabel(fieldKey);
      }
    }
    return null;
  };
  
  const isOptionDisabled = (currentField: string, option: MappingOption): boolean => {
    const locationLevelMappings: MappingOption[] = ['warehouse', 'aisle', 'rack', 'shelf', 'bin'];
    if (!locationLevelMappings.includes(option)) return false;
    
    for (const [fieldKey, mapping] of Object.entries(fieldMappings)) {
      if (fieldKey !== currentField && mapping === option) {
        return true;
      }
    }
    return false;
  };
  
  const validateMappings = () => {
    const warnings: string[] = [];
    const errors: string[] = [];
    
    // Check if Sub Analysis Code is concatenated but no User Sorts are mapped
    if (fieldMappings.subAnalysisCode === 'concatenated') {
      const userSortsMapped = ['userSort1', 'userSort2', 'userSort3', 'userSort4', 'userSort5', 'userSort6']
        .some(key => {
          const mapping = fieldMappings[key];
          return mapping !== 'not-used' && ['warehouse', 'aisle', 'rack', 'shelf', 'bin'].includes(mapping);
        });
      
      if (!userSortsMapped) {
        warnings.push('Sub Analysis Code is set to "Concatenated from User Sorts" but no User Sort fields are mapped to location levels');
      }
    }
    
    // Check if Warehouse (Level 1) is mapped
    const warehouseMapped = Object.values(fieldMappings).includes('warehouse');
    if (!warehouseMapped) {
      warnings.push('No fields are mapped to Warehouse (Level 1). At least one field should be mapped to the warehouse level');
    }
    
    // Check for duplicate mappings (shouldn't happen with disabled options, but double-check)
    const locationLevelMappings: MappingOption[] = ['warehouse', 'aisle', 'rack', 'shelf', 'bin'];
    const usedMappings = new Map<MappingOption, string[]>();
    
    for (const [fieldKey, mapping] of Object.entries(fieldMappings)) {
      if (locationLevelMappings.includes(mapping)) {
        if (!usedMappings.has(mapping)) {
          usedMappings.set(mapping, []);
        }
        usedMappings.get(mapping)!.push(getFieldLabel(fieldKey));
      }
    }
    
    for (const [mapping, fields] of usedMappings.entries()) {
      if (fields.length > 1) {
        const mappingLabel = mapping.charAt(0).toUpperCase() + mapping.slice(1);
        errors.push(`${mappingLabel} is mapped to multiple fields: ${fields.join(', ')}`);
      }
    }
    
    return { warnings, errors, isValid: errors.length === 0 && warnings.length === 0 };
  };
  
  const validation = validateMappings();
  
  // Generate preview based on current mapping
  const generatePreview = () => {
    const sampleData = {
      subAnalysisCode: 'MAIN-A01-R05-S02-B12',
      userSort1: 'MAIN',
      userSort2: 'A01',
      userSort3: 'R05',
      userSort4: 'S02',
      userSort5: 'B12',
      userSort6: '',
      binNumber: 'BIN-001',
      sortKey: 'SORT-KEY-1'
    };
    
    const mappedLevels: { level: string; value: string }[] = [];
    
    // Map each field to its corresponding level
    const fieldToSampleData: Record<string, string> = {
      subAnalysisCode: sampleData.subAnalysisCode,
      userSort1: sampleData.userSort1,
      userSort2: sampleData.userSort2,
      userSort3: sampleData.userSort3,
      userSort4: sampleData.userSort4,
      userSort5: sampleData.userSort5,
      userSort6: sampleData.userSort6,
      binNumber: sampleData.binNumber,
      sortKey: sampleData.sortKey
    };
    
    const levelNames: Record<MappingOption, string> = {
      'warehouse': 'Warehouse',
      'aisle': 'Aisle',
      'rack': 'Rack',
      'shelf': 'Shelf',
      'bin': 'Bin',
      'not-used': '',
      'concatenated': '',
      'product-category': '',
      'custom-field': ''
    };
    
    // Collect mapped levels in order
    for (const [fieldKey, mapping] of Object.entries(fieldMappings)) {
      if (['warehouse', 'aisle', 'rack', 'shelf', 'bin'].includes(mapping)) {
        const value = fieldToSampleData[fieldKey];
        if (value) {
          mappedLevels.push({
            level: levelNames[mapping as MappingOption],
            value: value
          });
        }
      }
    }
    
    // Sort by hierarchy order
    const hierarchyOrder = ['Warehouse', 'Aisle', 'Rack', 'Shelf', 'Bin'];
    mappedLevels.sort((a, b) => hierarchyOrder.indexOf(a.level) - hierarchyOrder.indexOf(b.level));
    
    return { sampleData, mappedLevels };
  };
  
  const preview = generatePreview();
  
  // Sample configuration scenarios
  const sampleConfigurations = {
    'multipleWarehouses': {
      name: 'Multiple Warehouses with Full Location Hierarchy',
      description: 'Maps warehouse to User Sort 1, then Aisle/Rack/Shelf/Bin to remaining sorts',
      icon: Building2,
      tag: 'Most Common',
      tagVariant: 'default' as const,
      mappings: {
        'subAnalysisCode': 'not-used' as MappingOption,
        'userSort1': 'warehouse' as MappingOption,
        'userSort2': 'aisle' as MappingOption,
        'userSort3': 'rack' as MappingOption,
        'userSort4': 'shelf' as MappingOption,
        'userSort5': 'bin' as MappingOption,
        'userSort6': 'not-used' as MappingOption,
        'binNumber': 'not-used' as MappingOption,
        'sortKey': 'not-used' as MappingOption,
      }
    },
    'singleWarehouse': {
      name: 'Single Warehouse with Locations',
      description: 'Uses User Sort 1 for zones/areas within a single warehouse',
      icon: Warehouse,
      tag: 'Small Business',
      tagVariant: 'secondary' as const,
      mappings: {
        'subAnalysisCode': 'not-used' as MappingOption,
        'userSort1': 'warehouse' as MappingOption,
        'userSort2': 'aisle' as MappingOption,
        'userSort3': 'rack' as MappingOption,
        'userSort4': 'shelf' as MappingOption,
        'userSort5': 'bin' as MappingOption,
        'userSort6': 'not-used' as MappingOption,
        'binNumber': 'not-used' as MappingOption,
        'sortKey': 'not-used' as MappingOption,
      }
    },
    'warehouseOnly': {
      name: 'Warehouse Only - No Detailed Locations',
      description: 'Maps only warehouse codes, no detailed bin locations',
      icon: Package,
      tag: 'Simple Setup',
      tagVariant: 'outline' as const,
      mappings: {
        'subAnalysisCode': 'not-used' as MappingOption,
        'userSort1': 'warehouse' as MappingOption,
        'userSort2': 'not-used' as MappingOption,
        'userSort3': 'not-used' as MappingOption,
        'userSort4': 'not-used' as MappingOption,
        'userSort5': 'not-used' as MappingOption,
        'userSort6': 'not-used' as MappingOption,
        'binNumber': 'not-used' as MappingOption,
        'sortKey': 'not-used' as MappingOption,
      }
    },
    'financials': {
      name: 'Access Financials Standard',
      description: 'Default mapping for Access Financials warehouse and location fields',
      icon: Database,
      tag: 'Financials',
      tagVariant: 'secondary' as const,
      mappings: {
        'subAnalysisCode': 'concatenated' as MappingOption,
        'userSort1': 'warehouse' as MappingOption,
        'userSort2': 'aisle' as MappingOption,
        'userSort3': 'rack' as MappingOption,
        'userSort4': 'shelf' as MappingOption,
        'userSort5': 'bin' as MappingOption,
        'userSort6': 'not-used' as MappingOption,
        'binNumber': 'not-used' as MappingOption,
        'sortKey': 'not-used' as MappingOption,
      }
    }
  };
  
  // Handler functions
  const handleSaveMappingConfiguration = () => {
    if (!validation.isValid) {
      toast.error("Cannot save configuration", {
        description: "Please fix validation errors before saving."
      });
      return;
    }
    
    // TODO: Implement actual save logic
    toast.success("Migration mapping saved successfully", {
      description: "Your ERP migration configuration has been saved."
    });
  };
  
  const handleExportConfiguration = () => {
    const config = {
      erpSystem,
      erpDatabase,
      fieldMappings,
      usedForBatchTracking,
      usedForVariants,
      exportedAt: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    a.download = `erp-migration-mapping-${timestamp}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success("Configuration exported", {
      description: "Your mapping configuration has been downloaded."
    });
  };
  
  const handleLoadSampleConfig = (configKey: string) => {
    setSelectedSampleConfig(configKey);
    setShowConfirmLoadDialog(true);
  };
  
  const confirmLoadSampleConfig = () => {
    if (!selectedSampleConfig) return;
    
    const config = sampleConfigurations[selectedSampleConfig as keyof typeof sampleConfigurations];
    if (config) {
      setFieldMappings(config.mappings);
      toast.success(`Loaded ${config.name} configuration`, {
        description: "Your mapping configuration has been updated."
      });
    }
    
    setShowConfirmLoadDialog(false);
    setShowSampleConfigModal(false);
    setSelectedSampleConfig(null);
  };
  
  // Load ERP integration info from localStorage
  useEffect(() => {
    const financialsEnabled = localStorage.getItem("integration.financials.enabled") === "true";
    const dimensionsEnabled = localStorage.getItem("integration.dimensions.enabled") === "true";
    const financialsDb = localStorage.getItem("integration.financials.database");
    const dimensionsDb = localStorage.getItem("integration.dimensions.database");
    
    if (financialsEnabled && financialsDb) {
      setErpSystem("Access Financials");
      setErpDatabase(financialsDb);
    } else if (dimensionsEnabled && dimensionsDb) {
      setErpSystem("Access Dimensions");
      setErpDatabase(dimensionsDb);
    }
  }, []);
  
  // Handle mode toggle
  const handleModeToggle = useCallback(
    (newIsLive: boolean) => {
      setMode(newIsLive ? "live" : "demo");
    },
    [setMode],
  );
  const getDefaultConfig = (): WarehouseHierarchyConfig => {
    return {
      ...DEFAULT_HIERARCHY_CONFIG,
      numberOfLevels: 5,
      levels: DEFAULT_HIERARCHY_CONFIG.levels.map((level) => ({
        ...level,
        isActive: true,
      })),
    };
  };

  const getInitialConfigFromLocalStorage = (): WarehouseHierarchyConfig => {
    const saved = localStorage.getItem("warehouse-hierarchy-config");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          ...parsed,
          numberOfLevels: 5,
          levels: parsed.levels.map((level: HierarchyLevel) => ({
            ...level,
            isActive: true,
          })),
        };
      } catch (e) {
        console.error("Failed to parse saved config:", e);
      }
    }
    return getDefaultConfig();
  };

  const [config, setConfig] = useState<WarehouseHierarchyConfig>(getDefaultConfig);
  const [originalConfig, setOriginalConfig] = useState<WarehouseHierarchyConfig>(getDefaultConfig);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [editingLevel, setEditingLevel] = useState<number | null>(null);

  const fetchConfigFromAPI = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetchGraphQL<{
        locationHierarchy: {
          locationHierarchyConfig: LocationHierarchyLevelFromAPI[];
        };
      }>(GET_LOCATION_HIERARCHY_CONFIG);

      if (response.errors) {
        console.error("GraphQL errors:", response.errors);
        toast.error("Failed to load configuration from server");
        return;
      }

      if (response.data?.locationHierarchy?.locationHierarchyConfig) {
        const apiLevels = response.data.locationHierarchy.locationHierarchyConfig;
        const levels: HierarchyLevel[] = apiLevels.map((level) => ({
          level: level.levelNumber,
          displayName: level.displayName,
          codePrefix: level.codePrefix || "",
          isActive: level.isActive,
        }));

        const newConfig: WarehouseHierarchyConfig = {
          numberOfLevels: 5,
          levels,
        };

        setConfig(newConfig);
        setOriginalConfig(newConfig);
      }
    } catch (error) {
      console.error("Failed to fetch config from API:", error);
      toast.error("Failed to load configuration from server");
    } finally {
      setIsLoading(false);
    }
  }, [fetchGraphQL]);

  useEffect(() => {
    if (isLive) {
      fetchConfigFromAPI();
    } else {
      const localConfig = getInitialConfigFromLocalStorage();
      setConfig(localConfig);
      setOriginalConfig(localConfig);
    }
  }, [isLive, fetchConfigFromAPI]);

  const handleEditLevel = (levelNumber: number) => {
    setEditingLevel(levelNumber);
  };

  const handleCancelEdit = () => {
    // Revert changes by reloading from originalConfig
    setConfig({ ...originalConfig });
    setEditingLevel(null);
  };

  const handleSaveLevel = async (levelNumber: number) => {
    const level = config.levels.find((l) => l.level === levelNumber);
    if (!level || !level.displayName.trim()) {
      toast.error("Display name is required");
      return;
    }

    const displayNames = config.levels.map((l) => l.displayName.toLowerCase().trim());
    const duplicateDisplayName =
      displayNames.filter((name) => name === level.displayName.toLowerCase().trim()).length > 1;

    if (duplicateDisplayName) {
      toast.error("Display name must be unique across all levels");
      return;
    }

    if (level.codePrefix) {
      const codePrefixes = config.levels.map((l) => l.codePrefix.toUpperCase().trim()).filter((p) => p);
      const duplicatePrefix =
        codePrefixes.filter((prefix) => prefix === level.codePrefix.toUpperCase().trim()).length > 1;

      if (duplicatePrefix) {
        toast.error("Code prefix must be unique across all levels");
        return;
      }
    }

    if (isLive) {
      setIsSaving(true);
      try {
        const response = await fetchGraphQL<{
          locationHierarchy: {
            locationHierarchyConfig: {
              updateLevel: LocationHierarchyLevelFromAPI;
            };
          };
        }>(UPDATE_LOCATION_HIERARCHY_LEVEL, {
          level: {
            levelID: levelNumber,
            levelNumber: levelNumber,
            displayName: level.displayName,
            codePrefix: level.codePrefix || null,
            isActive: level.isActive,
          },
        });

        if (response.errors) {
          console.error("GraphQL errors:", response.errors);
          toast.error("Failed to save configuration to server");
          return;
        }

        setOriginalConfig(config);
        setEditingLevel(null);
        // Dispatch event to notify other components (e.g., left menu) to refresh their hierarchy config
        window.dispatchEvent(new CustomEvent('hierarchy-config-updated'));
        toast.success(`Level ${levelNumber} updated successfully (Live)`);
      } catch (error) {
        console.error("Failed to save config to API:", error);
        toast.error("Failed to save configuration to server");
      } finally {
        setIsSaving(false);
      }
    } else {
      localStorage.setItem("warehouse-hierarchy-config", JSON.stringify(config));
      setOriginalConfig(config);
      setEditingLevel(null);
      toast.success(`Level ${levelNumber} updated successfully (Demo)`);
    }
  };

  const handleLevelChange = (levelNumber: number, field: keyof HierarchyLevel, value: string | boolean) => {
    const updatedLevels = config.levels.map((level) =>
      level.level === levelNumber ? { ...level, [field]: value } : level,
    );

    setConfig({
      ...config,
      levels: updatedLevels,
    });
  };

  const getErrorForField = (field: string): string | undefined => {
    return validationErrors.find((e) => e.field === field)?.message;
  };

  const locationPreview = generateLocationCodePreview(config);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">Loading configuration...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2">
          <Warehouse className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold tracking-tight">Locations</h1>
          {isDemo ? (
            <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
              Demo Mode
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              Live Mode
            </Badge>
          )}
          <div className="ml-auto flex items-center gap-2">
            <TestTube2 className={`h-4 w-4 ${isDemo ? "text-yellow-600" : "text-muted-foreground"}`} />
            <Switch checked={isLive} onCheckedChange={handleModeToggle} aria-label="Toggle live mode" />
            <Monitor className={`h-4 w-4 ${isLive ? "text-green-600" : "text-muted-foreground"}`} />
          </div>
        </div>
        <p className="text-muted-foreground mt-2">
          Configure your warehouse location hierarchy and ERP migration settings
          {isDemo && " (changes saved to browser storage)"}
          {isLive && " (changes saved to database)"}
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="hierarchy" className="flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            Location Hierarchy
          </TabsTrigger>
          <TabsTrigger value="erp-mapper" className="flex items-center gap-2">
            <ArrowLeftRight className="h-4 w-4" />
            ERP Migration Mapper
          </TabsTrigger>
        </TabsList>

        {/* Location Hierarchy Configuration Tab */}
        <TabsContent value="hierarchy" className="space-y-4">
          {/* Info Banner */}
          <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950 dark:border-blue-800">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-blue-900 dark:text-blue-100">Flexible Location Hierarchy</p>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Customize the terminology for your 5-level location hierarchy to match your warehouse operations.
                Changes will apply to all location creation and management interfaces throughout the system.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Configuration Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Location Hierarchy Configuration
          </CardTitle>
          <CardDescription>
            Customize the display names and code prefixes for each of the 5 hierarchy levels
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Level Configuration */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold">Level Configuration</h3>
              <Badge variant="secondary">5 Levels</Badge>
            </div>

            <div className="space-y-4">
              {config.levels.map((level) => (
                <div key={level.level} className="p-4 rounded-lg border bg-card border-border">
                  <div className="space-y-4">
                    {/* Level Header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant="default">Level {level.level}</Badge>
                      </div>
                      {editingLevel === level.level ? (
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="outline" onClick={handleCancelEdit}>
                            Cancel
                          </Button>
                          <Button size="sm" onClick={() => handleSaveLevel(level.level)} disabled={isSaving}>
                            {isSaving ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                Saving...
                              </>
                            ) : (
                              "Save"
                            )}
                          </Button>
                        </div>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => handleEditLevel(level.level)}>
                          Edit
                        </Button>
                      )}
                    </div>

                    {editingLevel === level.level ? (
                      /* Edit Mode */
                      <div className="grid gap-4 md:grid-cols-2">
                        {/* Display Name */}
                        <div className="space-y-2">
                          <Label htmlFor={`level${level.level}DisplayName`} className="flex items-center gap-1">
                            Display Name
                            <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id={`level${level.level}DisplayName`}
                            value={level.displayName}
                            onChange={(e) => handleLevelChange(level.level, "displayName", e.target.value)}
                            placeholder={LEVEL_PLACEHOLDER_EXAMPLES[level.level - 1]}
                            className={getErrorForField(`level${level.level}DisplayName`) ? "border-red-500" : ""}
                          />
                          {getErrorForField(`level${level.level}DisplayName`) && (
                            <p className="text-sm text-red-500">{getErrorForField(`level${level.level}DisplayName`)}</p>
                          )}
                          <p className="text-xs text-muted-foreground">Customer-facing name for this level</p>
                        </div>

                        {/* Code Prefix */}
                        <div className="space-y-2">
                          <Label htmlFor={`level${level.level}CodePrefix`} className="flex items-center gap-2">
                            Code Prefix (Optional)
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <p>Optional prefix for location codes. Example: "A" for Aisle, "R" for Rack</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </Label>
                          <Input
                            id={`level${level.level}CodePrefix`}
                            value={level.codePrefix}
                            onChange={(e) => handleLevelChange(level.level, "codePrefix", e.target.value.toUpperCase())}
                            placeholder="e.g., WH, A, R"
                            maxLength={4}
                          />
                          <p className="text-xs text-muted-foreground">3-4 characters recommended</p>
                        </div>
                      </div>
                    ) : (
                      /* View Mode */
                      <div className="grid gap-4 md:grid-cols-2">
                        {/* Display Name */}
                        <div className="space-y-2">
                          <Label className="text-muted-foreground">Display Name</Label>
                          <div className="text-base font-medium">{level.displayName}</div>
                        </div>

                        {/* Code Prefix */}
                        <div className="space-y-2">
                          <Label className="text-muted-foreground">Code Prefix</Label>
                          <div className="text-base font-medium">
                            {level.codePrefix || <span className="text-muted-foreground italic">Not set</span>}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Capacity Tracking - Future Implementation */}
          <div className="space-y-4 pt-4 border-t opacity-60 pointer-events-none relative">
            <div className="absolute top-0 right-0 z-10">
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                Future Implementation
              </Badge>
            </div>
            <h3 className="text-lg font-semibold">Capacity Management</h3>
            <div className="flex items-center justify-between p-4 rounded-lg border bg-card">
              <div className="space-y-1 flex-1">
                <Label htmlFor="capacityTracking" className="text-base font-medium">
                  Enable capacity tracking for locations
                </Label>
                <p className="text-sm text-muted-foreground">
                  Track maximum capacity and current utilization for each location. Disable if you don't need this
                  feature.
                </p>
              </div>
              <Switch id="capacityTracking" checked={false} disabled />
            </div>
          </div>

          {/* Location Code Preview */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="text-lg font-semibold">Location Code Preview</h3>
            <Card className="border-green-200 bg-green-50 dark:bg-green-950 dark:border-green-800">
              <CardContent className="pt-6">
                <div className="flex gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                  <div className="space-y-2 flex-1">
                    <p className="text-sm font-medium text-green-900 dark:text-green-100">Example Location Code</p>
                    <div className="flex items-center gap-2">
                      <code className="px-3 py-2 bg-white dark:bg-gray-900 rounded border text-lg font-mono">
                        {locationPreview}
                      </code>
                    </div>
                    <p className="text-sm text-green-700 dark:text-green-300">
                      Based on your settings, location codes will follow this format. The numbers (01, 02, etc.) will
                      increment automatically.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        {/* ERP Migration Mapper Tab */}
        <TabsContent value="erp-mapper" className="space-y-4">
          {/* ERP Source Information */}
          {erpSystem && erpDatabase && (
            <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950 dark:border-blue-800">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  {erpSystem === "Access Financials" ? (
                    <Database className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  ) : (
                    <LinkIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-100">Source ERP System</p>
                      <Badge variant="outline" className="bg-white dark:bg-blue-900 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700">
                        {erpSystem}
                      </Badge>
                    </div>
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      Database: <span className="font-medium">{erpDatabase}</span>
                    </p>
                    <p className="text-xs text-blue-600 dark:text-blue-400">
                      Configured in Integration settings
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Info Banner */}
          <Card className="border-purple-200 bg-purple-50 dark:bg-purple-950 dark:border-purple-800">
            <CardContent className="pt-6">
              <div className="flex gap-3">
                <Info className="h-5 w-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-purple-900 dark:text-purple-100">ERP Migration Assistant</p>
                  <p className="text-sm text-purple-700 dark:text-purple-300">
                    Map your legacy ERP location codes to Stock Evo's location hierarchy. This tool helps you migrate existing location data by creating mapping rules between old and new location structures.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Migration Mapping Interface */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ArrowLeftRight className="h-5 w-5" />
                Location Code Mapping
              </CardTitle>
              <CardDescription>
                Define how your legacy ERP location codes map to Stock Evo's hierarchy structure
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Legacy Location Code Format */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold">Legacy Location Format</h3>
                  <Badge variant="secondary">Step 1</Badge>
                </div>
                
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="legacyFormat">Legacy Location Code Format</Label>
                    <Input
                      id="legacyFormat"
                      placeholder="e.g., WH01-A01-R01-S01-B01"
                      className="font-mono"
                    />
                    <p className="text-xs text-muted-foreground">
                      Enter an example of your legacy location code format
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="delimiter">Delimiter Character</Label>
                    <Input
                      id="delimiter"
                      placeholder="e.g., - or /"
                      maxLength={1}
                      className="font-mono"
                    />
                    <p className="text-xs text-muted-foreground">
                      Character used to separate hierarchy levels
                    </p>
                  </div>
                </div>
              </div>

              {/* Field Mapping Configuration */}
              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold">Field Mapping Configuration</h3>
                  <Badge variant="secondary">Step 2</Badge>
                </div>
                
                <p className="text-sm text-muted-foreground">
                  Map {erpSystem || 'ERP'} fields to Stock Evo location hierarchy levels
                </p>
                
                <div className="rounded-lg border bg-card p-6">
                  <div className="space-y-4">
                    {/* Header Row */}
                    <div className="grid grid-cols-2 gap-6 pb-3 border-b">
                      <div className="font-semibold text-sm">{erpSystem || 'ERP'} Field</div>
                      <div className="font-semibold text-sm">Stock Evo Location Level</div>
                    </div>
                    
                    {/* Sub Analysis Code */}
                    <div className="grid grid-cols-2 gap-6 items-start">
                      <div className="space-y-1 pt-2">
                        <Label className="font-medium">Sub Analysis Code</Label>
                        <p className="text-xs text-muted-foreground">Primary location identifier</p>
                      </div>
                      <Select
                        value={fieldMappings.subAnalysisCode}
                        onValueChange={(value) => setFieldMappings(prev => ({ ...prev, subAnalysisCode: value as MappingOption }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="not-used">Not Used</SelectItem>
                          <SelectItem value="warehouse" disabled={isOptionDisabled('subAnalysisCode', 'warehouse')}>
                            Warehouse (Level 1){isOptionDisabled('subAnalysisCode', 'warehouse') && ` (Already mapped to ${getMappedField('warehouse')})`}
                          </SelectItem>
                          <SelectItem value="aisle" disabled={isOptionDisabled('subAnalysisCode', 'aisle')}>
                            Aisle (Level 2){isOptionDisabled('subAnalysisCode', 'aisle') && ` (Already mapped to ${getMappedField('aisle')})`}
                          </SelectItem>
                          <SelectItem value="rack" disabled={isOptionDisabled('subAnalysisCode', 'rack')}>
                            Rack (Level 3){isOptionDisabled('subAnalysisCode', 'rack') && ` (Already mapped to ${getMappedField('rack')})`}
                          </SelectItem>
                          <SelectItem value="shelf" disabled={isOptionDisabled('subAnalysisCode', 'shelf')}>
                            Shelf (Level 4){isOptionDisabled('subAnalysisCode', 'shelf') && ` (Already mapped to ${getMappedField('shelf')})`}
                          </SelectItem>
                          <SelectItem value="bin" disabled={isOptionDisabled('subAnalysisCode', 'bin')}>
                            Bin (Level 5){isOptionDisabled('subAnalysisCode', 'bin') && ` (Already mapped to ${getMappedField('bin')})`}
                          </SelectItem>
                          <SelectItem value="concatenated">Concatenated from User Sorts</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* User Sort 1 */}
                    <div className="grid grid-cols-2 gap-6 items-start">
                      <div className="space-y-1 pt-2">
                        <Label className="font-medium">User Sort 1</Label>
                        <p className="text-xs text-muted-foreground">Custom sort field 1</p>
                      </div>
                      <Select
                        value={fieldMappings.userSort1}
                        onValueChange={(value) => setFieldMappings(prev => ({ ...prev, userSort1: value as MappingOption }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="not-used">Not Used</SelectItem>
                          <SelectItem value="warehouse" disabled={isOptionDisabled('userSort1', 'warehouse')}>
                            Warehouse (Level 1){isOptionDisabled('userSort1', 'warehouse') && ` (Already mapped to ${getMappedField('warehouse')})`}
                          </SelectItem>
                          <SelectItem value="aisle" disabled={isOptionDisabled('userSort1', 'aisle')}>
                            Aisle (Level 2){isOptionDisabled('userSort1', 'aisle') && ` (Already mapped to ${getMappedField('aisle')})`}
                          </SelectItem>
                          <SelectItem value="rack" disabled={isOptionDisabled('userSort1', 'rack')}>
                            Rack (Level 3){isOptionDisabled('userSort1', 'rack') && ` (Already mapped to ${getMappedField('rack')})`}
                          </SelectItem>
                          <SelectItem value="shelf" disabled={isOptionDisabled('userSort1', 'shelf')}>
                            Shelf (Level 4){isOptionDisabled('userSort1', 'shelf') && ` (Already mapped to ${getMappedField('shelf')})`}
                          </SelectItem>
                          <SelectItem value="bin" disabled={isOptionDisabled('userSort1', 'bin')}>
                            Bin (Level 5){isOptionDisabled('userSort1', 'bin') && ` (Already mapped to ${getMappedField('bin')})`}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* User Sort 2 */}
                    <div className="grid grid-cols-2 gap-6 items-start">
                      <div className="space-y-1 pt-2">
                        <Label className="font-medium">User Sort 2</Label>
                        <p className="text-xs text-muted-foreground">Custom sort field 2</p>
                      </div>
                      <Select
                        value={fieldMappings.userSort2}
                        onValueChange={(value) => setFieldMappings(prev => ({ ...prev, userSort2: value as MappingOption }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="not-used">Not Used</SelectItem>
                          <SelectItem value="warehouse" disabled={isOptionDisabled('userSort2', 'warehouse')}>
                            Warehouse (Level 1){isOptionDisabled('userSort2', 'warehouse') && ` (Already mapped to ${getMappedField('warehouse')})`}
                          </SelectItem>
                          <SelectItem value="aisle" disabled={isOptionDisabled('userSort2', 'aisle')}>
                            Aisle (Level 2){isOptionDisabled('userSort2', 'aisle') && ` (Already mapped to ${getMappedField('aisle')})`}
                          </SelectItem>
                          <SelectItem value="rack" disabled={isOptionDisabled('userSort2', 'rack')}>
                            Rack (Level 3){isOptionDisabled('userSort2', 'rack') && ` (Already mapped to ${getMappedField('rack')})`}
                          </SelectItem>
                          <SelectItem value="shelf" disabled={isOptionDisabled('userSort2', 'shelf')}>
                            Shelf (Level 4){isOptionDisabled('userSort2', 'shelf') && ` (Already mapped to ${getMappedField('shelf')})`}
                          </SelectItem>
                          <SelectItem value="bin" disabled={isOptionDisabled('userSort2', 'bin')}>
                            Bin (Level 5){isOptionDisabled('userSort2', 'bin') && ` (Already mapped to ${getMappedField('bin')})`}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* User Sort 3 */}
                    <div className="grid grid-cols-2 gap-6 items-start">
                      <div className="space-y-1 pt-2">
                        <Label className="font-medium">User Sort 3</Label>
                        <p className="text-xs text-muted-foreground">Custom sort field 3</p>
                      </div>
                      <Select
                        value={fieldMappings.userSort3}
                        onValueChange={(value) => setFieldMappings(prev => ({ ...prev, userSort3: value as MappingOption }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="not-used">Not Used</SelectItem>
                          <SelectItem value="warehouse" disabled={isOptionDisabled('userSort3', 'warehouse')}>
                            Warehouse (Level 1){isOptionDisabled('userSort3', 'warehouse') && ` (Already mapped to ${getMappedField('warehouse')})`}
                          </SelectItem>
                          <SelectItem value="aisle" disabled={isOptionDisabled('userSort3', 'aisle')}>
                            Aisle (Level 2){isOptionDisabled('userSort3', 'aisle') && ` (Already mapped to ${getMappedField('aisle')})`}
                          </SelectItem>
                          <SelectItem value="rack" disabled={isOptionDisabled('userSort3', 'rack')}>
                            Rack (Level 3){isOptionDisabled('userSort3', 'rack') && ` (Already mapped to ${getMappedField('rack')})`}
                          </SelectItem>
                          <SelectItem value="shelf" disabled={isOptionDisabled('userSort3', 'shelf')}>
                            Shelf (Level 4){isOptionDisabled('userSort3', 'shelf') && ` (Already mapped to ${getMappedField('shelf')})`}
                          </SelectItem>
                          <SelectItem value="bin" disabled={isOptionDisabled('userSort3', 'bin')}>
                            Bin (Level 5){isOptionDisabled('userSort3', 'bin') && ` (Already mapped to ${getMappedField('bin')})`}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* User Sort 4 */}
                    <div className="grid grid-cols-2 gap-6 items-start">
                      <div className="space-y-1 pt-2">
                        <Label className="font-medium">User Sort 4</Label>
                        <p className="text-xs text-muted-foreground">Custom sort field 4</p>
                      </div>
                      <Select
                        value={fieldMappings.userSort4}
                        onValueChange={(value) => setFieldMappings(prev => ({ ...prev, userSort4: value as MappingOption }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="not-used">Not Used</SelectItem>
                          <SelectItem value="warehouse" disabled={isOptionDisabled('userSort4', 'warehouse')}>
                            Warehouse (Level 1){isOptionDisabled('userSort4', 'warehouse') && ` (Already mapped to ${getMappedField('warehouse')})`}
                          </SelectItem>
                          <SelectItem value="aisle" disabled={isOptionDisabled('userSort4', 'aisle')}>
                            Aisle (Level 2){isOptionDisabled('userSort4', 'aisle') && ` (Already mapped to ${getMappedField('aisle')})`}
                          </SelectItem>
                          <SelectItem value="rack" disabled={isOptionDisabled('userSort4', 'rack')}>
                            Rack (Level 3){isOptionDisabled('userSort4', 'rack') && ` (Already mapped to ${getMappedField('rack')})`}
                          </SelectItem>
                          <SelectItem value="shelf" disabled={isOptionDisabled('userSort4', 'shelf')}>
                            Shelf (Level 4){isOptionDisabled('userSort4', 'shelf') && ` (Already mapped to ${getMappedField('shelf')})`}
                          </SelectItem>
                          <SelectItem value="bin" disabled={isOptionDisabled('userSort4', 'bin')}>
                            Bin (Level 5){isOptionDisabled('userSort4', 'bin') && ` (Already mapped to ${getMappedField('bin')})`}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* User Sort 5 */}
                    <div className="grid grid-cols-2 gap-6 items-start">
                      <div className="space-y-1 pt-2">
                        <Label className="font-medium">User Sort 5</Label>
                        <p className="text-xs text-muted-foreground">Custom sort field 5</p>
                      </div>
                      <Select
                        value={fieldMappings.userSort5}
                        onValueChange={(value) => setFieldMappings(prev => ({ ...prev, userSort5: value as MappingOption }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="not-used">Not Used</SelectItem>
                          <SelectItem value="warehouse" disabled={isOptionDisabled('userSort5', 'warehouse')}>
                            Warehouse (Level 1){isOptionDisabled('userSort5', 'warehouse') && ` (Already mapped to ${getMappedField('warehouse')})`}
                          </SelectItem>
                          <SelectItem value="aisle" disabled={isOptionDisabled('userSort5', 'aisle')}>
                            Aisle (Level 2){isOptionDisabled('userSort5', 'aisle') && ` (Already mapped to ${getMappedField('aisle')})`}
                          </SelectItem>
                          <SelectItem value="rack" disabled={isOptionDisabled('userSort5', 'rack')}>
                            Rack (Level 3){isOptionDisabled('userSort5', 'rack') && ` (Already mapped to ${getMappedField('rack')})`}
                          </SelectItem>
                          <SelectItem value="shelf" disabled={isOptionDisabled('userSort5', 'shelf')}>
                            Shelf (Level 4){isOptionDisabled('userSort5', 'shelf') && ` (Already mapped to ${getMappedField('shelf')})`}
                          </SelectItem>
                          <SelectItem value="bin" disabled={isOptionDisabled('userSort5', 'bin')}>
                            Bin (Level 5){isOptionDisabled('userSort5', 'bin') && ` (Already mapped to ${getMappedField('bin')})`}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* User Sort 6 */}
                    <div className="grid grid-cols-2 gap-6 items-start">
                      <div className="space-y-1 pt-2">
                        <Label className="font-medium">User Sort 6</Label>
                        <p className="text-xs text-muted-foreground">Custom sort field 6</p>
                      </div>
                      <Select
                        value={fieldMappings.userSort6}
                        onValueChange={(value) => setFieldMappings(prev => ({ ...prev, userSort6: value as MappingOption }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="not-used">Not Used</SelectItem>
                          <SelectItem value="warehouse" disabled={isOptionDisabled('userSort6', 'warehouse')}>
                            Warehouse (Level 1){isOptionDisabled('userSort6', 'warehouse') && ` (Already mapped to ${getMappedField('warehouse')})`}
                          </SelectItem>
                          <SelectItem value="aisle" disabled={isOptionDisabled('userSort6', 'aisle')}>
                            Aisle (Level 2){isOptionDisabled('userSort6', 'aisle') && ` (Already mapped to ${getMappedField('aisle')})`}
                          </SelectItem>
                          <SelectItem value="rack" disabled={isOptionDisabled('userSort6', 'rack')}>
                            Rack (Level 3){isOptionDisabled('userSort6', 'rack') && ` (Already mapped to ${getMappedField('rack')})`}
                          </SelectItem>
                          <SelectItem value="shelf" disabled={isOptionDisabled('userSort6', 'shelf')}>
                            Shelf (Level 4){isOptionDisabled('userSort6', 'shelf') && ` (Already mapped to ${getMappedField('shelf')})`}
                          </SelectItem>
                          <SelectItem value="bin" disabled={isOptionDisabled('userSort6', 'bin')}>
                            Bin (Level 5){isOptionDisabled('userSort6', 'bin') && ` (Already mapped to ${getMappedField('bin')})`}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Bin Number */}
                    <div className="grid grid-cols-2 gap-6 items-start">
                      <div className="space-y-1 pt-2">
                        <Label className="font-medium">Bin Number</Label>
                        <p className="text-xs text-muted-foreground">Physical bin identifier</p>
                      </div>
                      <Select
                        value={fieldMappings.binNumber}
                        onValueChange={(value) => setFieldMappings(prev => ({ ...prev, binNumber: value as MappingOption }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="not-used">Not Used</SelectItem>
                          <SelectItem value="warehouse" disabled={isOptionDisabled('binNumber', 'warehouse')}>
                            Warehouse (Level 1){isOptionDisabled('binNumber', 'warehouse') && ` (Already mapped to ${getMappedField('warehouse')})`}
                          </SelectItem>
                          <SelectItem value="aisle" disabled={isOptionDisabled('binNumber', 'aisle')}>
                            Aisle (Level 2){isOptionDisabled('binNumber', 'aisle') && ` (Already mapped to ${getMappedField('aisle')})`}
                          </SelectItem>
                          <SelectItem value="rack" disabled={isOptionDisabled('binNumber', 'rack')}>
                            Rack (Level 3){isOptionDisabled('binNumber', 'rack') && ` (Already mapped to ${getMappedField('rack')})`}
                          </SelectItem>
                          <SelectItem value="shelf" disabled={isOptionDisabled('binNumber', 'shelf')}>
                            Shelf (Level 4){isOptionDisabled('binNumber', 'shelf') && ` (Already mapped to ${getMappedField('shelf')})`}
                          </SelectItem>
                          <SelectItem value="bin" disabled={isOptionDisabled('binNumber', 'bin')}>
                            Bin (Level 5){isOptionDisabled('binNumber', 'bin') && ` (Already mapped to ${getMappedField('bin')})`}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Sort Key */}
                    <div className="grid grid-cols-2 gap-6 items-start">
                      <div className="space-y-1 pt-2">
                        <Label className="font-medium">Sort Key</Label>
                        <p className="text-xs text-muted-foreground">Sorting/grouping key</p>
                      </div>
                      <Select
                        value={fieldMappings.sortKey}
                        onValueChange={(value) => setFieldMappings(prev => ({ ...prev, sortKey: value as MappingOption }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="not-used">Not Used</SelectItem>
                          <SelectItem value="warehouse" disabled={isOptionDisabled('sortKey', 'warehouse')}>
                            Warehouse (Level 1){isOptionDisabled('sortKey', 'warehouse') && ` (Already mapped to ${getMappedField('warehouse')})`}
                          </SelectItem>
                          <SelectItem value="aisle" disabled={isOptionDisabled('sortKey', 'aisle')}>
                            Aisle (Level 2){isOptionDisabled('sortKey', 'aisle') && ` (Already mapped to ${getMappedField('aisle')})`}
                          </SelectItem>
                          <SelectItem value="rack" disabled={isOptionDisabled('sortKey', 'rack')}>
                            Rack (Level 3){isOptionDisabled('sortKey', 'rack') && ` (Already mapped to ${getMappedField('rack')})`}
                          </SelectItem>
                          <SelectItem value="shelf" disabled={isOptionDisabled('sortKey', 'shelf')}>
                            Shelf (Level 4){isOptionDisabled('sortKey', 'shelf') && ` (Already mapped to ${getMappedField('shelf')})`}
                          </SelectItem>
                          <SelectItem value="bin" disabled={isOptionDisabled('sortKey', 'bin')}>
                            Bin (Level 5){isOptionDisabled('sortKey', 'bin') && ` (Already mapped to ${getMappedField('bin')})`}
                          </SelectItem>
                          <SelectItem value="product-category">Product Category</SelectItem>
                          <SelectItem value="custom-field">Custom Field</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                
                {/* Validation Status Indicator */}
                <div className="mt-6">
                  {validation.errors.length > 0 && (
                    <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-950 dark:border-red-800 p-4">
                      <div className="flex items-start gap-3">
                        <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 space-y-2">
                          <p className="text-sm font-semibold text-red-900 dark:text-red-100">Invalid configuration</p>
                          <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
                            {validation.errors.map((error, index) => (
                              <li key={index}>• {error}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {validation.errors.length === 0 && validation.warnings.length > 0 && (
                    <div className="rounded-lg border border-yellow-200 bg-yellow-50 dark:bg-yellow-950 dark:border-yellow-800 p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 space-y-2">
                          <p className="text-sm font-semibold text-yellow-900 dark:text-yellow-100">Review warnings</p>
                          <ul className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
                            {validation.warnings.map((warning, index) => (
                              <li key={index}>• {warning}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {validation.isValid && (
                    <div className="rounded-lg border border-green-200 bg-green-50 dark:bg-green-950 dark:border-green-800 p-4">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                        <p className="text-sm font-semibold text-green-900 dark:text-green-100">Mapping configuration is valid</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Advanced Tracking & Special Cases */}
              <div className="space-y-4 pt-6">
                <div className="rounded-lg border bg-muted/30 p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Tag className="h-5 w-5 text-muted-foreground" />
                    <h3 className="text-lg font-semibold">Advanced Tracking & Special Cases</h3>
                  </div>
                  
                  <div className="space-y-4">
                    {/* Warning banner when either checkbox is checked */}
                    {(usedForBatchTracking || usedForVariants) && (
                      <div className="rounded-lg border border-yellow-200 bg-yellow-50 dark:bg-yellow-950 dark:border-yellow-800 p-4">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                          <p className="text-sm font-medium text-yellow-900 dark:text-yellow-100">
                            Special tracking detected: These features require manual migration planning. Please contact your implementation specialist.
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {/* Checkbox 1: Batch/Lot Tracking */}
                    <div className="flex items-start gap-3">
                      <Checkbox 
                        id="batch-tracking"
                        checked={usedForBatchTracking}
                        onCheckedChange={(checked) => setUsedForBatchTracking(checked as boolean)}
                        className="mt-1"
                      />
                      <div className="space-y-1 flex-1">
                        <Label 
                          htmlFor="batch-tracking" 
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                        >
                          We used Sub Analysis/Location fields for batch/lot number tracking
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          Batch/lot tracking is planned for Phase 2. We'll notify you when this feature is available for migration.
                        </p>
                      </div>
                    </div>
                    
                    {/* Checkbox 2: Product Variants */}
                    <div className="flex items-start gap-3">
                      <Checkbox 
                        id="product-variants"
                        checked={usedForVariants}
                        onCheckedChange={(checked) => setUsedForVariants(checked as boolean)}
                        className="mt-1"
                      />
                      <div className="space-y-1 flex-1">
                        <Label 
                          htmlFor="product-variants" 
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                        >
                          We used Sub Analysis/Location fields for product variants (color/size/style)
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          We recommend creating separate SKUs for each variant. Contact support for guidance on variant migration strategies.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Preview Your Mapping */}
              <div className="space-y-4 pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Eye className="h-5 w-5 text-muted-foreground" />
                  <h3 className="text-lg font-semibold">Preview Your Mapping</h3>
                </div>
                
                {!validation.isValid && validation.warnings.includes('No fields are mapped to Warehouse (Level 1). At least one field should be mapped to the warehouse level') ? (
                  <div className="rounded-lg border border-yellow-200 bg-yellow-50 dark:bg-yellow-950 dark:border-yellow-800 p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                      <p className="text-sm font-medium text-yellow-900 dark:text-yellow-100">
                        No warehouse mapping configured - preview unavailable
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-lg border bg-card p-6">
                    <div className="space-y-6">
                      {/* Example Source Data */}
                      <div>
                        <h4 className="text-sm font-semibold mb-3 text-muted-foreground">Example Source Data ({erpSystem || 'ERP System'})</h4>
                        <div className="rounded-lg bg-muted/50 p-4 space-y-2 font-mono text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Sub Analysis Code:</span>
                            <span className="font-semibold">{preview.sampleData.subAnalysisCode}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">User Sort 1:</span>
                            <span className="font-semibold">{preview.sampleData.userSort1}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">User Sort 2:</span>
                            <span className="font-semibold">{preview.sampleData.userSort2}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">User Sort 3:</span>
                            <span className="font-semibold">{preview.sampleData.userSort3}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">User Sort 4:</span>
                            <span className="font-semibold">{preview.sampleData.userSort4}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">User Sort 5:</span>
                            <span className="font-semibold">{preview.sampleData.userSort5}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">User Sort 6:</span>
                            <span className="font-semibold">{preview.sampleData.userSort6 || '(empty)'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Bin Number:</span>
                            <span className="font-semibold">{preview.sampleData.binNumber}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Sort Key:</span>
                            <span className="font-semibold">{preview.sampleData.sortKey}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Maps to Stock Evo Location */}
                      <div>
                        <h4 className="text-sm font-semibold mb-3 text-muted-foreground">Maps to Stock Evo Location</h4>
                        {preview.mappedLevels.length > 0 ? (
                          <div className="space-y-4">
                            {/* Breadcrumb-style hierarchy */}
                            <div className="rounded-lg bg-primary/5 border border-primary/20 p-4">
                              <div className="flex items-center flex-wrap gap-2">
                                <MapPin className="h-4 w-4 text-primary flex-shrink-0" />
                                {preview.mappedLevels.map((level, index) => (
                                  <div key={index} className="flex items-center gap-2">
                                    {index > 0 && <ArrowRight className="h-4 w-4 text-muted-foreground" />}
                                    <div className="flex items-center gap-1">
                                      <span className="text-xs font-medium text-muted-foreground">{level.level}:</span>
                                      <span className="font-mono font-semibold text-sm">{level.value}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                            
                            {/* Full Location Code */}
                            <div className="rounded-lg bg-muted/50 p-4">
                              <div className="flex items-start gap-3">
                                <div className="flex-1">
                                  <p className="text-xs font-medium text-muted-foreground mb-1">Full Location Code</p>
                                  <p className="font-mono text-lg font-bold">{preview.mappedLevels.map(l => l.value).join('-')}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="rounded-lg border border-dashed border-muted-foreground/30 p-6 text-center">
                            <p className="text-sm text-muted-foreground">No location levels mapped yet. Configure field mappings above to see preview.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Bulk Import Section */}
              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold">Bulk Import</h3>
                  <Badge variant="secondary">Step 3</Badge>
                </div>
                
                <Card className="border-dashed">
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      <div className="text-center space-y-2">
                        <div className="flex justify-center">
                          <div className="p-3 rounded-full bg-muted">
                            <ArrowLeftRight className="h-6 w-6 text-muted-foreground" />
                          </div>
                        </div>
                        <div>
                          <p className="font-medium">Import Legacy Location Mappings</p>
                          <p className="text-sm text-muted-foreground">
                            Upload a CSV file with your legacy location codes to apply the mapping rules
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex justify-center gap-2">
                        <Button variant="outline">
                          Download Template
                        </Button>
                        <Button>
                          Upload CSV
                        </Button>
                      </div>
                      
                      <div className="text-center">
                        <p className="text-xs text-muted-foreground">
                          CSV format: LegacyCode, Notes (optional)
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-2 pt-6 border-t">
                <Button 
                  variant="ghost" 
                  onClick={() => setShowSampleConfigModal(true)}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Load Sample Configuration
                </Button>
                <Button 
                  variant="outline"
                  onClick={handleExportConfiguration}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export Configuration
                </Button>
                <Button 
                  onClick={handleSaveMappingConfiguration}
                  disabled={!validation.isValid}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Mapping Configuration
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Migration Statistics */}
          <Card>
            <CardHeader>
              <CardTitle>Migration Statistics</CardTitle>
              <CardDescription>
                Overview of your location migration progress
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="p-4 rounded-lg border bg-card">
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Total Legacy Locations</p>
                    <p className="text-2xl font-bold">0</p>
                  </div>
                </div>
                
                <div className="p-4 rounded-lg border bg-card">
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Successfully Mapped</p>
                    <p className="text-2xl font-bold text-green-600">0</p>
                  </div>
                </div>
                
                <div className="p-4 rounded-lg border bg-card">
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Requires Review</p>
                    <p className="text-2xl font-bold text-yellow-600">0</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Sample Configuration Modal */}
      <Dialog open={showSampleConfigModal} onOpenChange={setShowSampleConfigModal}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Load Sample Mapping Configuration</DialogTitle>
            <DialogDescription>
              Choose a pre-configured mapping scenario to get started quickly
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(sampleConfigurations).map(([key, config]) => {
              const IconComponent = config.icon;
              return (
                <Card 
                  key={key}
                  className="cursor-pointer hover:border-primary hover:shadow-md transition-all group relative"
                >
                  <CardContent className="pt-6">
                    <div className="flex flex-col h-full">
                      {/* Icon and Tag */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="p-3 rounded-lg bg-primary/10 text-primary">
                          <IconComponent className="h-6 w-6" />
                        </div>
                        <Badge variant={config.tagVariant}>{config.tag}</Badge>
                      </div>
                      
                      {/* Title and Description */}
                      <div className="flex-1 space-y-2 mb-4">
                        <h4 className="font-semibold text-base leading-tight">{config.name}</h4>
                        <p className="text-sm text-muted-foreground leading-relaxed">{config.description}</p>
                      </div>
                      
                      {/* Select Button */}
                      <Button 
                        className="w-full"
                        onClick={() => handleLoadSampleConfig(key)}
                      >
                        Select
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSampleConfigModal(false)}>
              <X className="h-4 w-4 mr-2" />
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmLoadDialog} onOpenChange={setShowConfirmLoadDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace Current Configuration?</AlertDialogTitle>
            <AlertDialogDescription>
              This will replace your current mapping configuration. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowConfirmLoadDialog(false);
              setSelectedSampleConfig(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmLoadSampleConfig}>
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
